import { ShieldCheck, Lock, Activity, ClipboardCheck, UtensilsCrossed, Zap, CalendarCheck, Star } from "lucide-react";

const HOSPITAL_IMG =
  "https://images.unsplash.com/photo-1551076805-e1869033e561?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const RESTAURANT_IMG =
  "https://images.unsplash.com/photo-1560053608-13721e0d69e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const blocks = [
  {
    id: "hospitals",
    label: "Sector 01",
    title: "Hospitals & Clinics",
    img: HOSPITAL_IMG,
    accent: "#00f0ff",
    desc: "Healthcare demands trust. We build secure, reliable digital platforms that present your facility as the professional institution it is — while automating intake, scheduling, and internal workflows.",
    points: [
      { icon: ShieldCheck, text: "Security-first architecture and data protection" },
      { icon: Lock, text: "Compliance-conscious design and handling" },
      { icon: Activity, text: "Reliability your patients can count on, around the clock" },
      { icon: ClipboardCheck, text: "Automated intake, scheduling & admin workflows" },
    ],
  },
  {
    id: "restaurants",
    label: "Sector 02",
    title: "Restaurants & Hospitality",
    img: RESTAURANT_IMG,
    accent: "#ff003c",
    desc: "Guests decide in seconds. We give your restaurant a stunning online presence with fast online ordering, effortless reservations, and apps that keep operations moving at the speed of service.",
    points: [
      { icon: UtensilsCrossed, text: "Online ordering built to convert hungry visitors" },
      { icon: CalendarCheck, text: "Reservations and waitlists without the friction" },
      { icon: Zap, text: "Speed-optimized pages that never lose a customer" },
      { icon: Star, text: "A customer experience that brings them back" },
    ],
  },
];

export default function WhoWeServe() {
  return (
    <section id="who-we-serve" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#ff003c]">// Who we serve</p>
          <h2 className="font-display mt-4 text-3xl font-black tracking-tight sm:text-4xl">
            Two industries. <span className="text-[#00f0ff] text-glow-cyan">Zero excuses.</span>
          </h2>
        </div>

        <div className="mt-16 space-y-8">
          {blocks.map((b, i) => (
            <article
              key={b.id}
              data-testid={`serve-${b.id}-card`}
              className={`grid overflow-hidden border border-white/10 bg-[#0a0a0a] lg:grid-cols-2 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="relative min-h-[280px]">
                <img src={b.img} alt={b.title} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/50" />
                <div
                  className="absolute inset-0 opacity-30"
                  style={{ background: `linear-gradient(135deg, ${b.accent}22, transparent 60%)` }}
                />
                <span
                  className="font-mono-tech absolute left-6 top-6 border px-3 py-1 text-[10px] uppercase tracking-widest"
                  style={{ borderColor: b.accent, color: b.accent }}
                >
                  {b.label}
                </span>
              </div>
              <div className="p-8 lg:p-14">
                <h3 className="font-display text-2xl font-bold tracking-tight">{b.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/55 md:text-base">{b.desc}</p>
                <ul className="mt-8 space-y-4">
                  {b.points.map((p) => (
                    <li key={p.text} className="flex items-start gap-3">
                      <p.icon size={18} className="mt-0.5 shrink-0" style={{ color: b.accent }} strokeWidth={1.75} />
                      <span className="text-sm text-white/70">{p.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
